/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { getUsages, getUsage, getSessionUsageId } from 'redux/reducers/usages';
import { withRouter, Route } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import actions from 'redux/actions';

import type {
  ReactRouterProps,
  Translate,
  Fetchable,
  SimplifiedUsage,
  Usage,
  ReduxState,
} from 'types';

import { parseInt } from 'lodash';
import protonesUtils from 'utils/protones';

import CrudSidebar from 'components/CrudSidebar';
import ScrollableContent from 'components/ScrollableContent';
import ToggleButton from 'components/ToggleButton';
import UsageIntro from './UsageIntro';
import UsageEdit from './UsageEdit';
import UsageShow from './UsageShow';
import styles from './styles.module.scss';

const mapStateToProps = (state: ReduxState, ownProps) => {
  const { usageId } = ownProps.match.params;
  const isNew = usageId === 'create';
  return {
    isNew,
    usages: getUsages(state),
    usage: getUsage(state),
    sessionUsageId: getSessionUsageId(state).data || undefined,
    usageId: !isNew ? parseInt(usageId) : undefined,
    ...ownProps,
  };
};

type Props = ReactRouterProps & {
  usages: Fetchable<SimplifiedUsage[]>,
  usage: Fetchable<Usage>,
  sessionUsageId: Fetchable<number | void>,
  isNew: boolean,
  usageId?: number,
  fetchUsageList: typeof actions.fetchUsageList,
  fetchUsage: typeof actions.fetchUsage,
  createUsage: typeof actions.createUsage,
  saveUsage: typeof actions.saveUsage,
  deleteUsage: typeof actions.deleteUsage,
  setSessionUsageId: typeof actions.setSessionUsageId,
  t: Translate,
  match: any,
};

class Usages extends React.Component<Props, void> {
  componentDidMount() {
    const { isNew, usageId, fetchUsage, fetchUsageList } = this.props;
    fetchUsageList();
    if (!isNew && usageId) {
      fetchUsage(usageId);
    }
  }

  componentWillReceiveProps({ usageId: newUsageId }: Props) {
    const { usageId, fetchUsage } = this.props;
    if (newUsageId && newUsageId !== usageId) {
      fetchUsage(newUsageId);
    }
  }

  _handleRefreshUsageList = () => {
    this.props.fetchUsageList();
  };

  _handleCreateUsage = () => {
    this.props.createUsage();
    protonesUtils.goTo(this.props, protonesUtils.routes.usages.create());
  };

  _handleSelectUsage = (id: number) => {
    this.props.fetchUsage(id);
    protonesUtils.goTo(this.props, protonesUtils.routes.usages.get(id));
  };

  _handleSetSessionUsage = (id: number) => {
    this.props.setSessionUsageId(id);
  };

  _handleEditUsage = (id: number) => {
    this.props.fetchUsage(id);
    protonesUtils.goTo(this.props, protonesUtils.routes.usages.edit(id));
  };

  _handleDeleteUsage = (id: number) => {
    //eslint-disable-next-line
    if (confirm(this.props.t('ARE_YOU_SURE'))) {
      this.props.deleteUsage(id, () => {
        this.props.fetchUsageList();
        protonesUtils.goTo(this.props, protonesUtils.routes.usages.find());
      });
    }
  };

  render() {
    const { usageId, isNew, usages, sessionUsageId, setSessionUsageId, t } = this.props;
    return (
      <div className={styles.root}>
        <CrudSidebar
          title={t('USAGE')}
          activeItemId={usageId}
          items={usages}
          buttons={item => {
            const isSessionUsage = item.id === sessionUsageId;
            return [
              <ToggleButton
                key={'star'}
                icon={isSessionUsage ? 'star' : 'star_outline'}
                tooltip={isSessionUsage ? t('SESSION_USAGE') : t('SELECT_AS_USAGE_SESSION')}
                disabled={!isSessionUsage}
                onClick={() => setSessionUsageId(isSessionUsage ? undefined : item.id)}
              />,
            ];
          }}
          onListRefresh={this._handleRefreshUsageList}
          onCreateItem={this._handleCreateUsage}
          onSelectItem={({ id }) => this._handleSelectUsage(id)}
          onEditItem={({ id }) => this._handleEditUsage(id)}
          onDeleteItem={({ id }) => this._handleDeleteUsage(id)}
        />
        <ScrollableContent>
          {isNew ? (
            <UsageEdit />
          ) : !usageId ? (
            <UsageIntro />
          ) : (
            <div style={{ height: '100%' }}>
              <Route exact path={`${this.props.match.url}/`} component={UsageShow} />
              <Route exact path={`${this.props.match.url}/edit`} component={UsageEdit} />
            </div>
          )}
        </ScrollableContent>
      </div>
    );
  }
}

export default withTranslation()(
  withRouter(
    connect(
      mapStateToProps,
      actions
    )(Usages)
  )
);
